/**
 * Synthetic live-aircraft generator for verifying the 3D wave (D2 §3.3) without
 * waiting on real traffic. Produces `AircraftFeature`s whose positions advance
 * with elapsed time so a harness can feed them through the same `renderAircraft`
 * path as the live feed — proving smooth SampledPositionProperty motion, the
 * LOD billboard↔glTF swap, heading-tracked orientation, and framerate under a
 * 100+ aircraft stress test. Pure (no Cesium / DOM) so it is unit-testable.
 */
import type { AircraftFeature } from '@tsr/shared-types';
import { interpolatePosition } from './aircraftInterp';

export interface SyntheticOptions {
  /** Number of aircraft to synthesize. */
  count: number;
  /** Centre of the cloud (defaults to roughly central Canada). */
  center?: { lat: number; lon: number };
  /** Half-extent of the initial scatter, degrees (default 2.5°). */
  spreadDeg?: number;
  /** Elapsed milliseconds since t0 — drives forward motion. */
  elapsedMs: number;
  /** ISO timestamp to stamp as last_seen_at (advances each poll → re-sample). */
  nowIso: string;
}

// A spread of roles + airframe types so the LOD covers jets, props and helis and
// the role palette is exercised. Helicopter entries (kind 'heli') get the rotor.
const FLEET: Array<{ role: string; type: string }> = [
  { role: 'water_bomber', type: 'CL-415' },
  { role: 'water_bomber', type: 'AT-802F Fire Boss' },
  { role: 'ccg_helo', type: 'CH-149 Cormorant' },
  { role: 'sar', type: 'CC-130 Hercules' },
  { role: 'sar_airlift', type: 'CC-115 Buffalo' },
  { role: 'air_ambulance', type: 'King Air 350' },
  { role: 'police', type: 'AS350' },
  { role: 'isr', type: 'PC-12 NG' },
  { role: 'heavy_lift', type: 'CH-147F Chinook' },
  { role: 'lentus_airlift', type: 'CH-146 Griffon' },
];

/**
 * Deterministic synthetic fleet at a given elapsed time. Each aircraft holds a
 * fixed heading + ground speed and is dead-reckoned forward from its scatter
 * origin, so successive calls (advancing `elapsedMs` + `nowIso`) read as a live
 * track. A few are marked `stale` / `based` to exercise the faded/held tiers.
 */
export function generateSyntheticAircraft(opts: SyntheticOptions): AircraftFeature[] {
  const center = opts.center ?? { lat: 52.0, lon: -97.0 };
  const spread = opts.spreadDeg ?? 2.5;
  const tSec = opts.elapsedMs / 1000;
  const out: AircraftFeature[] = [];
  const cols = Math.max(1, Math.ceil(Math.sqrt(opts.count)));
  for (let i = 0; i < opts.count; i++) {
    const fleet = FLEET[i % FLEET.length]!;
    // Scatter on a grid, jittered deterministically by index.
    const row = Math.floor(i / cols);
    const col = i % cols;
    const jx = ((i * 73) % 100) / 100 - 0.5;
    const jy = ((i * 137) % 100) / 100 - 0.5;
    const baseLat = center.lat + ((row / cols) * 2 - 1) * spread + jy * 0.3;
    const baseLon = center.lon + ((col / cols) * 2 - 1) * spread * 1.6 + jx * 0.3;
    const heading = (i * 47) % 360;
    const speedKt = 150 + (i % 7) * 35; // 150–360 kt
    const altM = 600 + (i % 9) * 900; // 600–7800 m
    const pos = interpolatePosition(baseLat, baseLon, heading, speedKt, tSec);
    // ~8% quiet transponders; every other quiet one is long-quiet 'based' so
    // the dimmest tier of the #245 ladder is exercised alongside 'stale'.
    const status = i % 26 === 0 ? 'based' : i % 13 === 0 ? 'stale' : 'live';
    const id = `synthetic-${i}`;
    out.push({
      type: 'Feature',
      id,
      geometry: { type: 'Point', coordinates: [pos.lon, pos.lat] },
      properties: {
        id,
        tail_number: `SYN${String(i).padStart(3, '0')}`,
        icao_hex: null,
        aircraft_type: fleet.type,
        aircraft_role: fleet.role,
        operator_org: 'Synthetic',
        operator_asset_id: null,
        altitude_m: altM,
        speed_kt: speedKt,
        heading_deg: heading,
        last_seen_at: opts.nowIso,
        tracking_status: status,
        hex_captured_from_live: false,
      },
    });
  }
  return out;
}
